import type { ChildModelApplicationRecord, ChildModelApplicationStatus } from '../types';
import { getSiteAuthRuntime, getStoredToken } from './bliss-app-auth';
import { requestPublicModelApplicationsApi } from './public-model-applications-api';

function getErrorMessage(payload: unknown, fallback: string): string {
  if (!payload || typeof payload !== 'object') return fallback;

  const source = payload as {
    error?: string | { message?: string };
    message?: string;
    data?: { message?: string };
  };

  if (typeof source.error === 'string' && source.error) return source.error;
  if (source.error && typeof source.error === 'object' && typeof source.error.message === 'string') {
    return source.error.message;
  }
  if (typeof source.message === 'string' && source.message) return source.message;
  if (source.data && typeof source.data.message === 'string' && source.data.message) return source.data.message;
  return fallback;
}

async function readResponseJson(response: Response): Promise<unknown> {
  return response.json().catch(() => null);
}

async function requestChildModelApplicationsApi<T>(path: string, options: RequestInit = {}): Promise<T> {
  const runtime = await getSiteAuthRuntime();
  const token = getStoredToken();
  const headers = new Headers(options.headers);
  const method = (options.method || 'GET').toUpperCase();
  const hasBody = options.body != null;

  if (!token) {
    throw new Error('You need to sign in before managing child model applications.');
  }

  if (hasBody && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  if (!headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${runtime.apiBaseUrl}${path}`, {
    ...options,
    method,
    headers,
  });

  const payload = await readResponseJson(response);
  if (!response.ok) {
    throw new Error(getErrorMessage(payload, 'The child model application service could not be reached right now.'));
  }

  const source = payload as { data?: T };
  return (source.data ?? payload) as T;
}

export async function getMyChildModelApplication(input: {
  eventId: string;
  ticketId: string;
}): Promise<ChildModelApplicationRecord | null> {
  const search = new URLSearchParams({
    event_id: input.eventId,
    ticket_id: input.ticketId,
  });

  const result = await requestChildModelApplicationsApi<{ application: ChildModelApplicationRecord | null }>(
    `/events/child-model-applications/me?${search.toString()}`,
  );

  return result.application ?? null;
}

export async function submitChildModelApplication(input: {
  eventId: string;
  ticketId: string;
  applicationMessage?: string;
  availabilityDetails?: string;
}): Promise<ChildModelApplicationRecord> {
  const result = await requestChildModelApplicationsApi<{ application: ChildModelApplicationRecord }>(
    '/events/child-model-applications',
    {
      method: 'POST',
      body: JSON.stringify({
        event_id: input.eventId,
        ticket_id: input.ticketId,
        application_message: input.applicationMessage?.trim() || '',
        availability_details: input.availabilityDetails?.trim() || '',
      }),
    },
  );

  return result.application;
}

export async function submitPublicChildModelApplication(input: {
  eventId: string;
  ticketId: string;
  childFirstName: string;
  childLastName: string;
  childBirthday: string;
  guardianName: string;
  guardianEmail: string;
  guardianPhone: string;
  guardianRelationship: string;
  city: string;
  state: string;
  instagramHandle?: string;
  experienceSummary?: string;
  allergiesOrAccommodations?: string;
  applicationMessage?: string;
  availabilityDetails?: string;
  participationConsent: boolean;
  mediaReleaseApproved?: boolean;
}): Promise<ChildModelApplicationRecord> {
  const result = await requestPublicModelApplicationsApi<{ application: ChildModelApplicationRecord }>(
    '/public/events/child-model-applications',
    {
      method: 'POST',
      body: JSON.stringify({
        event_id: input.eventId,
        ticket_id: input.ticketId,
        child_first_name: input.childFirstName.trim(),
        child_last_name: input.childLastName.trim(),
        child_birthday: input.childBirthday.trim(),
        guardian_name: input.guardianName.trim(),
        guardian_email: input.guardianEmail.trim(),
        guardian_phone: input.guardianPhone.trim(),
        guardian_relationship: input.guardianRelationship.trim(),
        city: input.city.trim(),
        state: input.state.trim(),
        instagram_handle: input.instagramHandle?.trim() || '',
        experience_summary: input.experienceSummary?.trim() || '',
        allergies_or_accommodations: input.allergiesOrAccommodations?.trim() || '',
        application_message: input.applicationMessage?.trim() || '',
        availability_details: input.availabilityDetails?.trim() || '',
        participation_consent: input.participationConsent,
        media_release_approved: input.mediaReleaseApproved ?? false,
      }),
    },
    'The child model application form could not be submitted right now.',
  );

  return result.application;
}

export async function listChildModelApplications(options: {
  eventId?: string;
  status?: ChildModelApplicationStatus | 'all';
  limit?: number;
} = {}): Promise<ChildModelApplicationRecord[]> {
  const search = new URLSearchParams();
  if (options.eventId) {
    search.set('event_id', options.eventId);
  }
  if (options.status && options.status !== 'all') {
    search.set('status', options.status);
  }
  if (typeof options.limit === 'number' && Number.isFinite(options.limit)) {
    search.set('limit', String(options.limit));
  }

  const result = await requestChildModelApplicationsApi<{ applications: ChildModelApplicationRecord[] }>(
    `/events/child-model-applications${search.toString() ? `?${search.toString()}` : ''}`,
  );

  return Array.isArray(result.applications) ? result.applications : [];
}

export async function updateChildModelApplicationStatus(
  applicationId: string,
  input: {
    status: ChildModelApplicationStatus;
    reviewNotes?: string;
  },
): Promise<ChildModelApplicationRecord> {
  const result = await requestChildModelApplicationsApi<{ application: ChildModelApplicationRecord }>(
    `/events/child-model-applications/${encodeURIComponent(applicationId)}`,
    {
      method: 'PUT',
      body: JSON.stringify({
        status: input.status,
        review_notes: input.reviewNotes?.trim() || '',
      }),
    },
  );

  return result.application;
}
